import * as UserAgentParser from 'ua-parser-js';
import { v4 as uuid } from 'uuid';

export const isNumber = (_: any) => typeof _ === 'number';
export const isString = (_: any) => typeof _ === 'string' || _ instanceof String;

export const isLocalStorageAvailable = (): boolean => {
    try {
        let storage = window.localStorage;
        let x = '__storage_test__';
        storage.setItem(x, x);
        storage.removeItem(x);
        return true;
    } catch (e) {
        return false;
    }
};

export function getLocalSettings(): any {
    const existingSettings = getCurrentSettings();
    const defaults = {
        senderId: uuid(),
        sessionStarted: new Date().toISOString(),
        gaClientId: getGoogleAnalyticsVisitorId(),
        roxanneVisitorId: getRoxanneVisitorId(),
    };
    const settings = { ...defaults, ...existingSettings };
    saveCurrentSettings(settings);

    return settings;
}

function getCurrentSettings() {
    try {
        if (isLocalStorageAvailable()) {
            const raw = localStorage.getItem('Conversations');
            if (raw) {
                return JSON.parse(raw);
            }
        }
    } catch (e) {
        // ignore
    }

    return {};
}

function saveCurrentSettings(settings: any) {
    try {
        if (isLocalStorageAvailable()) {
            localStorage.setItem('Conversations', JSON.stringify(settings));
        }
    } catch (e) {
        // ignore
    }
}

interface WindowWithGA extends Window {
    GoogleAnalyticsObject?: any;
    DIDataLayer?: any;
}
declare var window: WindowWithGA;

function getGoogleAnalyticsVisitorId(): string | void {
    let clientId;

    if (window.GoogleAnalyticsObject) {
        var gaName = window.GoogleAnalyticsObject;
    }
    if (gaName && window[gaName]) {
        window[gaName](function(tracker: any) {
            if (!tracker) {
                // site is not using a default tracker
                // client_id is the same for all trackers so just use the first one
                tracker = window[gaName].getAll()[0];
            }
            clientId = tracker.get('clientId');
        });
    }

    return clientId;
}

function getRoxanneVisitorId(): string | void {
    if (
        window.DIDataLayer &&
        window.DIDataLayer.user &&
        typeof window.DIDataLayer.user.get === 'function' &&
        window.DIDataLayer.user.get().visitor_id
    ) {
        return String(window.DIDataLayer.user.get().visitor_id);
    }
}

export function getVersionMajor (version: string | undefined) {
    const parts = version && isString(version) ? version.split('.') : [];

    if (parts.length > 0 && /^\d+$/.test(parts[0])) {
        return parseInt(parts[0], 10);
    } else {
        return null;
    }
}

export function formatCurrency(val: any, digits: number = 2) {
    if (val === undefined) {
        return 'N/A';
    }

    try {
        const numVal = Number(val);
        return isNaN(numVal)
            ? val
            : numVal.toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: digits,
                  minimumFractionDigits: digits,
              });
    } catch (e) {
        return val;
    }
}

/**
 * Generate a generic rate limiting function
 */
export function rateLimit(fn: any, delay: number, context?: any): (...args: any[]) => any {
    let queue: any[] = [];
    let timer: any = null;

    function processQueue() {
        const item = queue.shift();
        if (item) {
            fn.apply(item.context, item.arguments);
        }
        if (queue.length === 0) {
            clearInterval(timer);
            timer = null;
        }
    }

    return function limited(...args: any[]) {
        if (args[0] === 'CLEAR_QUEUE') {
            queue = [];
            return;
        }
        queue.push({
            context: context || null,
            arguments: [].slice.call(args),
        });

        if (!timer) {
            processQueue(); // start immediately on the first invocation
            timer = setInterval(processQueue, delay);
        }
    };
}

export function sendDomEvent(detail: any, eventName: string = 'di_conv_event') {
    document.dispatchEvent(new CustomEvent(eventName, { detail }));
}

// RFC 5724 sms
// https://stackoverflow.com/questions/6480462/how-to-pre-populate-the-sms-body-text-via-an-html-link
export function smsLink(phoneNumber: string, body?: string) {
    const os = (new UserAgentParser()).getOS();
    let sms = `sms:${phoneNumber}`;

    if (body) {
        if (os.name && os.name === 'iOS') {
            const versionMajor = getVersionMajor(os.version);

            if (versionMajor && versionMajor < 8) {
                sms = `${sms};body=${encodeURIComponent(body)}`;
            } else {
                sms = `${sms}&body=${encodeURIComponent(body)}`;
            }
        } else {
            sms = `${sms}?body=${encodeURIComponent(body)}`;
        }
    }

    return sms;
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/util/index.ts